// Sound banks for granular ("ocean grain") excitation. A bank resolves to a mono
// Float32Array recording that the engine slices into grains to excite the modal banks
// (see resonator-kernel makeGrain / strikeGrain). Two kinds:
//   - 'procedural': generated in-code (zero bytes shipped) — the always-available default.
//   - 'file':       a CC0/NOAA recording fetched + decoded LAZILY on first selection, so the
//                   cold-payload budget (<2 MB gz) is untouched. None are bundled yet; the user
//                   drops real recordings into public/sound-banks/ and adds an entry below.
import { mulberry32, type Rng } from '@shared/rng';

export interface SoundBank {
  /** stable id stored in scenes + used as the UI <option> value */
  id: string;
  /** display name */
  name: string;
  kind: 'procedural' | 'file';
  /** file banks: URL relative to the site root (e.g. 'sound-banks/reef.webm') */
  url?: string;
  /** file banks: licensing/credit string (CC0 source, NOAA, etc.) */
  attribution?: string | undefined;
}

/** Sentinel UI value meaning "no grain bank — grain events fall back to noise strikes". */
export const NO_BANK = 'none';

/**
 * Registry. Index order is stable (it derives each bank's engine-side numeric id), so only
 * ever APPEND. The procedural surf bank is always first and needs no asset or licensing.
 */
export const BANKS: SoundBank[] = [
  { id: 'surf', name: 'Surf (synth)', kind: 'procedural' },
  // Public-domain test banks (safe to host/redistribute). Replace/extend with your own picks; see
  // docs/sound-bank-sources.md. These let you ear-tune the grain engine before sourcing more.
  {
    id: 'shore',
    name: 'Shore (PD)',
    kind: 'file',
    url: 'sound-banks/shore.webm',
    attribution: 'Wikimedia Commons "Waves.ogg" by Dsw4 — Public Domain',
  },
  {
    id: 'hydrophone',
    name: 'Hydrophone (PD)',
    kind: 'file',
    url: 'sound-banks/hydrophone.webm',
    attribution: 'Wikimedia Commons "Killer whale residents broadband.ogg" — US NPS, Public Domain',
  },
  // --- Drop more CC0 / NOAA ocean recordings here (lazy-loaded, not bundled) ---
];

export function getBank(id: string): SoundBank | undefined {
  return BANKS.find((b) => b.id === id);
}

/**
 * Engine-side numeric id for a bank (the kernel keys loaded buffers by number). Index+1, so
 * 0 is never used by a real bank. Returns 0 for an unknown id.
 */
export function bankNumericId(id: string): number {
  const i = BANKS.findIndex((b) => b.id === id);
  return i < 0 ? 0 : i + 1;
}

/** Average N channels into a single mono buffer. Pure — the testable heart of `toMono`. */
export function mixToMono(channels: Float32Array[]): Float32Array {
  const ch = channels.filter((c) => c && c.length > 0);
  if (ch.length === 0) return new Float32Array(0);
  // Copy (not alias) the single channel: callers transfer this buffer to the worklet, and aliasing
  // a decoded AudioBuffer's channel store would detach Web-Audio-owned memory (spec doesn't allow).
  if (ch.length === 1) return ch[0].slice();
  const len = ch[0].length;
  const out = new Float32Array(len);
  for (const c of ch) {
    const n = Math.min(len, c.length);
    for (let i = 0; i < n; i++) out[i] += c[i];
  }
  const inv = 1 / ch.length;
  for (let i = 0; i < len; i++) out[i] *= inv;
  return out;
}

/** Downmix a decoded AudioBuffer to mono. */
export function toMono(buf: AudioBuffer): Float32Array {
  const channels: Float32Array[] = [];
  for (let c = 0; c < buf.numberOfChannels; c++) channels.push(buf.getChannelData(c));
  return mixToMono(channels);
}

/**
 * Generate a synthetic "surf" buffer: low brown-noise rumble + broadband foam hiss, shaped by
 * slow overlapping swell envelopes so different grain offsets sound different (some land in a
 * crest, some in a trough). Deterministic given `rng`. Peak-normalized to ~0.9. This is real,
 * usable grain content that needs no licensing — the placeholder until CC0 recordings are added.
 */
export function makeSurfBuffer(seconds: number, fs: number, rng: Rng): Float32Array {
  const n = Math.max(1, Math.round(seconds * fs));
  const out = new Float32Array(n);
  let brown = 0;
  let foamLP = 0;
  let peak = 0;
  for (let i = 0; i < n; i++) {
    const white = rng() * 2 - 1;
    // leaky-integrated brown noise -> low rumble
    brown = (brown + 0.025 * white) * 0.997;
    // gently low-passed white -> foam/hiss band
    foamLP += 0.45 * (white - foamLP);
    const t = i / fs;
    // overlapping slow swells (coprime-ish rates) -> a wave bed that breathes
    const swell =
      0.45 +
      0.3 * Math.sin(2 * Math.PI * 0.07 * t) +
      0.18 * Math.sin(2 * Math.PI * 0.123 * t + 1.7) +
      0.12 * Math.sin(2 * Math.PI * 0.31 * t + 0.4);
    const s = (brown * 7 + foamLP * 0.6) * Math.max(0, swell);
    out[i] = s;
    const a = Math.abs(s);
    if (a > peak) peak = a;
  }
  if (peak > 0) {
    const g = 0.9 / peak;
    for (let i = 0; i < n; i++) out[i] *= g;
  }
  return out;
}

/** Default length of the procedural surf bank (long enough that grain offsets vary widely). */
const SURF_SECONDS = 8;

/**
 * Resolve a bank to its mono buffer. Procedural banks are generated; file banks are fetched +
 * decoded (lazy). `rng` seeds procedural generation deterministically.
 */
export async function loadBankBuffer(
  ctx: AudioContext,
  bank: SoundBank,
  rng: Rng = mulberry32(0x0cea4),
): Promise<Float32Array> {
  if (bank.kind === 'procedural') {
    return makeSurfBuffer(SURF_SECONDS, ctx.sampleRate, rng);
  }
  if (!bank.url) throw new Error(`sound bank '${bank.id}' has no url`);
  const res = await fetch(bank.url);
  if (!res.ok) throw new Error(`sound bank '${bank.id}' fetch failed: ${res.status}`);
  const bytes = await res.arrayBuffer();
  const decoded = await ctx.decodeAudioData(bytes);
  return toMono(decoded);
}
